import { useEffect, useRef, useState } from "react";
import { Toast } from "./components/ui";
import Login from "./pages/Login";
import Shell from "./pages/Shell";
import BranchPicker from "./pages/BranchPicker";
import {
  api,
  setActiveBranch as apiSetActiveBranch,
  setWriteLock,
  setAuthToken,
  setOnUnauthorized
} from "./api/client";
import { useTheme } from "./utils/theme";
import { cabangAwal, cabangUntukPeran } from "./utils/peranCabang";

const STORAGE_KEY = "kip_portal_user";
const BRANCH_STORAGE_KEY = "kip_portal_active_branch";
const ROLE_STORAGE_KEY = "kip_portal_active_role";
// Menyegarkan akun saat kembali ke layar paling sering sekali per menit.
const JEDA_SEGAR_MS = 60 * 1000;

function App() {
  const [user, setUser] = useState(null);
  const [activeBranch, setActiveBranchState] = useState(null);
  const [activeRole, setActiveRoleState] = useState(null);
  const [toast, setToast] = useState(null);
  const [ready, setReady] = useState(false);
  const { theme, toggleTheme } = useTheme();
  // Nilai terbaru untuk pendengar yang dipasang sekali (kembali ke layar) dan untuk jawaban
  // server yang datang belakangan.
  const terkini = useRef({ user: null, role: null, branch: null });
  terkini.current = { user, role: activeRole, branch: activeBranch };
  const terakhirSegar = useRef(0);

  // Setel cabang aktif: state (App), header API (client.js), dan localStorage (objek utk tampilan).
  function chooseBranch(branch) {
    setActiveBranchState(branch || null);
    apiSetActiveBranch(branch?.id ?? null);
    try {
      if (branch) localStorage.setItem(BRANCH_STORAGE_KEY, JSON.stringify(branch));
      else localStorage.removeItem(BRANCH_STORAGE_KEY);
    } catch {
      /* abaikan */
    }
  }

  // Setel ROLE aktif (untuk akun multi-role) — menentukan portal yang ditampilkan.
  function chooseRole(role) {
    setActiveRoleState(role || null);
    try {
      if (role) localStorage.setItem(ROLE_STORAGE_KEY, role);
      else localStorage.removeItem(ROLE_STORAGE_KEY);
    } catch {
      /* abaikan */
    }
  }
  // Ganti peran → cabang ikut disesuaikan: tetap bila cabang ini juga boleh untuk peran baru,
  // otomatis bila hanya ada satu, selain itu pilih dulu. Peran per cabang: trainer BR01 yang
  // team leader BR07 berpindah dari BR01 ke BR07 saat memilih Team Leader.
  function switchRole(role) {
    chooseRole(role);
    chooseBranch(cabangAwal(user, role, activeBranch));
  }

  // Ambil ulang data akun (peran & daftar cabang) dari server. Daftar cabang dari login bisa
  // basi: sesi bertahan sampai 30 hari, sedangkan cabang tempat ia ditunjuk menggantikan
  // kelas — atau peran per cabangnya — bisa berubah kapan saja. Tanpa ini, trainer yang
  // ditunjuk menggantikan kelas di cabang LAIN tak bisa membuka kelas itu sampai ia keluar
  // lalu masuk lagi. Peran & cabang aktif ikut disesuaikan bila tak lagi berlaku.
  // Gagal (offline, sesi lama tanpa token) → data tersimpan tetap dipakai.
  async function segarkanAkun() {
    if (!terkini.current.user) return null;
    terakhirSegar.current = Date.now();
    let r;
    try {
      r = await api.getMe();
    } catch {
      return null;
    }
    const { user: lama, role, branch } = terkini.current;   // sesudah menunggu: keadaan terbaru
    if (!lama || !r || !r.success || !r.user) return null;   // sudah keluar, atau jawabannya kosong
    const baru = { ...lama, ...r.user };
    setUser(baru);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(baru));
    } catch {
      /* abaikan */
    }
    const roles = baru.roles || [];
    const peran = roles.includes(role) ? role : (roles[0] || null);
    if (peran !== role) chooseRole(peran);
    chooseBranch(cabangAwal(baru, peran, branch));
    return baru;
  }

  // Pindah ke cabang tempat ia menggantikan kelas (tombol di halaman Absensi). Daftar cabang
  // disegarkan dulu supaya cabang itu tercatat seperti cabang lainnya.
  async function bukaCabang(cabang) {
    // Tanpa id, header X-Branch-Id ikut kosong dan server membaca SEMUA cabang.
    if (!cabang || !cabang.id) return;
    const baru = await segarkanAkun();
    const daftar = (baru || terkini.current.user || {}).branches || [];
    chooseBranch(
      daftar.find((b) => b.id === cabang.id)
        || { id: cabang.id, code: cabang.code, name: cabang.name, roles: ["trainer"], coverage: true }
    );
  }

  useEffect(() => {
    const savedUser = localStorage.getItem(STORAGE_KEY);

    if (savedUser) {
      try {
        const u = JSON.parse(savedUser);
        setUser(u);

        // Pulihkan ROLE aktif (validasi ada di roles akun; default role pertama).
        const roles = u.roles || [];
        let savedRole = null;
        try { savedRole = localStorage.getItem(ROLE_STORAGE_KEY); } catch { savedRole = null; }
        const role = roles.includes(savedRole) ? savedRole : (roles[0] || null);
        chooseRole(role);

        // Pulihkan cabang aktif bila masih boleh untuk user & peran ini; 1 pilihan → otomatis;
        // selain itu null (0 cabang → legacy tanpa header; lebih dari satu → picker).
        let savedBranch = null;
        try {
          savedBranch = JSON.parse(localStorage.getItem(BRANCH_STORAGE_KEY) || "null");
        } catch {
          savedBranch = null;
        }
        chooseBranch(cabangAwal(u, role, savedBranch));
      } catch {
        localStorage.removeItem(STORAGE_KEY);
      }
    }

    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Segarkan akun sekali setelah sesi tersimpan dipulihkan, lalu setiap kembali ke layar
  // (tab/aplikasi dibuka lagi) — paling sering sekali per JEDA_SEGAR_MS.
  useEffect(() => {
    if (ready && terkini.current.user) segarkanAkun();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);
  useEffect(() => {
    const kembali = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - terakhirSegar.current < JEDA_SEGAR_MS) return;
      segarkanAkun();
    };
    document.addEventListener("visibilitychange", kembali);
    window.addEventListener("focus", kembali);
    return () => {
      document.removeEventListener("visibilitychange", kembali);
      window.removeEventListener("focus", kembali);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleLogin(nextUser) {
    setUser(nextUser);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(nextUser));

    // Role aktif = peran yang dipilih di layar login (bila valid), else role pertama
    // akun. Akun SATU peran tak pernah melihat pilihan — Login mengirim perannya
    // langsung. Akun multi-peran memilih kartunya dulu, baru onLogin dipanggil.
    const roles = nextUser.roles || [];
    const role =
      nextUser.activeRole && roles.includes(nextUser.activeRole)
        ? nextUser.activeRole
        : roles[0] || null;
    chooseRole(role);

    // Satu cabang untuk peran ini → langsung masuk; selain itu null (0 → Shell legacy,
    // lebih dari satu → BranchPicker).
    chooseBranch(cabangAwal(nextUser, role, null));
  }

  function handleLogout() {
    // Cabut token di server (best-effort) lalu bersihkan sisi klien.
    api.logout().catch(() => {});
    setAuthToken(null);
    setUser(null);
    chooseBranch(null);
    chooseRole(null);
    localStorage.removeItem(STORAGE_KEY);
    // Reset URL ke root agar tak tertinggal di path halaman konten (routing ringan Shell).
    try { window.history.replaceState({}, "", "/"); } catch { /* abaikan */ }

    setToast({
      type: "success",
      message: "Logged out successfully"
    });
  }

  function switchBranch() {
    chooseBranch(null); // kembali ke pemilih cabang (tanpa logout)
  }

  // Super admin di mode "Semua Cabang" (belum memfilter) → aplikasi hanya-baca
  // untuk aksi per-cabang (dijaga di client.js). Operasi global tetap boleh.
  useEffect(() => {
    setWriteLock(!!user?.isSuperAdmin && !activeBranch);
  }, [user, activeBranch]);

  // Server menolak sesi (401, mis. setelah AUTH_ENFORCE dinyalakan & token lama/absen)
  // → paksa logout & kembali ke Login.
  useEffect(() => {
    setOnUnauthorized(() => {
      setAuthToken(null);
      setUser(null);
      chooseBranch(null);
      chooseRole(null);
      localStorage.removeItem(STORAGE_KEY);
      try { window.history.replaceState({}, "", "/"); } catch { /* abaikan */ }
      setToast({ type: "error", message: "Sesi berakhir. Silakan login ulang." });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!ready) return null;

  // Gate 3-tingkat: butuh pilih cabang bila user tergabung di cabang mana pun & belum memilih
  // (yang hanya punya satu pilihan sudah dipilihkan otomatis). Pilihannya cabang yang cocok
  // dengan PERAN AKTIF — kosong bila peran ini tak dipegang di cabang mana pun, dan layar
  // cabang lalu menawarkan peran lain. Super Admin DIKECUALIKAN — ia langsung masuk konsol
  // mode "Semua Cabang" dan memfilter lewat dropdown di header (bukan picker layar penuh).
  const needsBranchPick =
    user &&
    !user.isSuperAdmin &&
    (user.branches?.length || 0) > 0 &&
    !activeBranch;

  return (
    <>
      <Toast toast={toast} setToast={setToast} />

      {!user ? (
        <Login
          onLogin={handleLogin}
          setToast={setToast}
          theme={theme}
          toggleTheme={toggleTheme}
        />
      ) : needsBranchPick ? (
        <BranchPicker
          user={user}
          branches={cabangUntukPeran(user, activeRole)}
          role={activeRole}
          onSwitchRole={switchRole}
          onSelectBranch={chooseBranch}
          onLogout={handleLogout}
          theme={theme}
          toggleTheme={toggleTheme}
        />
      ) : (
        // key={cabang-role} → ganti cabang/ROLE me-remount Shell agar view & data ter-reset.
        <Shell
          key={`${activeBranch?.id || "none"}-${activeRole || "none"}`}
          user={user}
          activeBranch={activeBranch}
          activeRole={activeRole}
          onSwitchBranch={switchBranch}
          onSwitchRole={switchRole}
          onSelectBranch={chooseBranch}
          onBukaCabang={bukaCabang}
          onLogout={handleLogout}
          setToast={setToast}
          theme={theme}
          toggleTheme={toggleTheme}
        />
      )}
    </>
  );
}

export default App;
